import {
  CommonEntitiesListResponse,
  Specialization,
  SpecializationCreationDto,
  SpecializationResponseDto,
  SpecializationUpdateDto,
} from '@docbay/schemas';
import axiosInstance from 'integration/api/axiosInstance';
import { fetchPaginatedSpecializationProps } from 'common/types/paginatedSpecializations';

export const SpecializationsAPI = {
  /**
   * Fetches all specializations from the server.
   *
   * @returns A promise that resolves with an array of Specialization objects.
   */
  fetchSpecializations: () =>
    axiosInstance.get<Specialization[]>('/specializations'),

  /**
   * Fetches a specific specialization by its ID.
   *
   * @param id - The ID of the specialization to fetch.
   * @returns A promise that resolves with a Specialization object.
   */
  fetchSpecializationsById: (id: string) =>
    axiosInstance.get<Specialization>(`/specializations/${id}`),

  /**
   * Updates an existing specialization.
   *
   * @param data - The update data for the specialization.
   * @param id - The ID of the specialization to update.
   * @returns A promise that resolves with a SpecializationResponseDto object.
   */
  editSpecialization: ({
    data,
    id,
  }: {
    data: SpecializationUpdateDto;
    id: string;
  }) =>
    axiosInstance.patch<SpecializationResponseDto>(
      `/specializations/${id}`,
      data,
    ),

  /**
   * Fetches a paginated list of specializations.
   *
   * @param props - The pagination and search parameters.
   * @returns A promise that resolves with a CommonEntitiesListResponse object containing an array of Specialization objects.
   */
  fetchPaginatedSpecializations: ({
    text,
    page,
    limit,
  }: fetchPaginatedSpecializationProps) => {
    const params = {
      ...(page ? { page } : {}),
      ...(limit ? { limit } : {}),
      ...(text ? { text } : {}),
      withDeactivated: true,
    };
    return axiosInstance.get<CommonEntitiesListResponse<Specialization>>(
      '/specializations/paginated-specializations',
      { params },
    );
  },

  /**
   * Adds a new specialization to the server.
   *
   * @param params - The creation data for the specialization.
   * @returns A promise that resolves with a SpecializationResponseDto object.
   */
  addSpecialization: (params: SpecializationCreationDto) =>
    axiosInstance.post<SpecializationResponseDto>('/specializations', params),

  /**
   * Deactivates an existing specialization by its ID.
   *
   * @param id - The ID of the specialization to deactivate.
   * @returns A promise that resolves with no data.
   */
  deactivateSpecialization: (id: string) =>
    axiosInstance.patch(`/specializations/${id}/deactivate`),

  /**
   * Activates an existing specialization by its ID.
   *
   * @param id - The ID of the specialization to activate.
   * @returns A promise that resolves with no data.
   */
  activateSpecialization: (id: string) =>
    axiosInstance.patch(`/specializations/${id}/reactivate`),
};
